/**
 * Global app state (Preact signals — no Redux ceremony for an app this
 * small) plus the navigation stack. Screens read/write these signals and
 * call the action functions below; they never talk to `api` directly for
 * anything that other screens also need to see (categories, household,
 * user), so every screen stays in sync without prop-drilling.
 */
import { signal } from '@preact/signals';
import { api, setNonce } from './api.js';
import { __ } from './i18n.js';

// --- Navigation --------------------------------------------------------
// Simple push/pop stack of {name, params}. Mirrors the v1 app's
// showScreen()/back-button UX, just driven by state instead of raw DOM
// toggling. `replace` swaps the top frame instead of pushing (used after
// login, and after an action that shouldn't leave a stale frame to go
// "back" into, e.g. task -> skip -> back to category after skipping).
export const navStack = signal([{ name: 'loading', params: {} }]);

export function navigate(name, params = {}) {
  navStack.value = [...navStack.value, { name, params }];
}

export function replace(name, params = {}) {
  navStack.value = [...navStack.value.slice(0, -1), { name, params }];
}

export function goBack() {
  if (navStack.value.length > 1) {
    navStack.value = navStack.value.slice(0, -1);
  }
}

export function resetTo(name, params = {}) {
  navStack.value = [{ name, params }];
}

// --- Session -------------------------------------------------------------
export const user = signal(null); // {id, username, household_id} | null
export const authChecked = signal(false);

// --- App data (loaded once logged in, refreshed on demand) --------------
export const categories = signal([]);
export const household = signal(null);
export const stats = signal(null);
export const calendarStatus = signal(null); // {providers: {google: {...}, microsoft: {...}}} | null

// --- Current task flow ----------------------------------------------------
export const flow = signal({ categoryId: null, availableMinutes: null });
export const currentTask = signal(null); // {task, remaining_count, warnings} | null
export const skipsThisSession = signal(0);

// WP-2: freshness signal for the desktop Aankomend panel (UpcomingPanel.jsx).
// Bumped by every screen right after a task mutation actually succeeds
// (create/update/delete/complete/skip/assign, and — since GET
// /tasks?status=active is an exact `status = 'active'` match on the
// backend, not an IN-list — also start, because starting a task moves it
// to 'in_progress' and silently drops it out of that result set). No
// screen currently calls api.tasks.snooze(), so there's no site to bump
// from for it yet. A plain counter rather than a boolean: the panel only
// needs "something changed since I last fetched", not what changed.
export const tasksVersion = signal(0);

export function bumpTasksVersion() {
  tasksVersion.value++;
}

// HH-4: same freshness-signal pattern for Boodschappen, bumped after every
// list/item mutation. Nothing in this package watches it itself (the
// screens that cause the bump already know to reload their own state
// directly, same as SubscriptionsScreen) — it exists now so HH-7's
// dashboard badge has a signal to react to when it lands, the same way
// tasksVersion existed in WP-2 before every consumer of it did.
export const listsVersion = signal(0);

export function bumpListsVersion() {
  listsVersion.value++;
}

// HH-6: same freshness-signal pattern for the Weekritme, bumped after
// every schedule mutation (create/update/delete, and the skip exception).
// WeekScreen watches it so returning from the item form shows the change
// without its own reload plumbing; HH-7's "vandaag op het weekritme" card
// gets a signal to react to for free.
export const scheduleVersion = signal(0);

export function bumpScheduleVersion() {
  scheduleVersion.value++;
}

// --- PWA install offer ---------------------------------------------------
// Bumped by lib/install.js whenever install availability changes (the
// browser handed us a beforeinstallprompt event, the app got installed, or
// the user snoozed the offer). Same counter idiom as the freshness signals
// above: lib/install.js owns the real state in module scope — it has to,
// because the listener is attached before the first render — and this is
// only how components learn to re-read it.
export const installState = signal(0);

// --- Quick Add (WP-6) ----------------------------------------------------
export const quickAddOpen = signal(false);
// Session-only "last category used to capture a task" (brief: "session
// signal in store.js") — never persisted, and explicitly cleared on
// logout() below so a shared device's next login doesn't inherit the
// previous person's last-used category.
export const lastCaptureCategoryId = signal(null);

export function openQuickAdd() {
  quickAddOpen.value = true;
}

export function closeQuickAdd() {
  quickAddOpen.value = false;
}

// Friction counter (roadmap §6: "after 2 skips in one session, show a
// gentle intervention"). Cleared whenever a fresh time budget is chosen
// (TimeScreen) or a task is completed — both are natural "this attempt is
// over" boundaries, unlike a skip's own refreshCurrentTask() re-fetch,
// which must NOT reset it or the 2-skip count could never be reached.
export function resetSkipSession() {
  skipsThisSession.value = 0;
}

// --- Toast -----------------------------------------------------------------
export const toastMessage = signal(null); // {text, type} | null
let toastTimer = null;

export function showToast(text, type = 'info') {
  if (toastTimer) clearTimeout(toastTimer);
  toastMessage.value = { text, type };
  toastTimer = setTimeout(() => {
    toastMessage.value = null;
  }, 3200);
}

export function friendlyError(err) {
  return err && err.message ? err.message : __('Er ging iets mis. Probeer het opnieuw.', 'etm');
}

// --- Actions ----------------------------------------------------------------

// Loads the data every logged-in screen expects to find already populated.
// Deliberately non-fatal: if it fails (flaky network right after auth), the
// user IS still genuinely logged in (the caller already confirmed that), so
// we still navigate them in rather than stranding them on a screen that
// disagrees with `user.value` — CategoryScreen's own mount effect retries
// loadCategories() whenever the signal is still empty.
async function loadSessionData() {
  try {
    await Promise.all([loadCategories(), loadHousehold()]);
  } catch (err) {
    showToast(friendlyError(err), 'error');
  }
}

export async function bootstrap() {
  // Consume BEFORE any await: the reset key must leave the address bar
  // even if auth/me hangs or fails, and a reload mid-boot must not
  // re-trigger the flow with a by-then-consumed key.
  const reset = consumeResetParams();
  try {
    const res = await api.auth.me();
    if (res.logged_in) {
      user.value = res.user;
      if (reset) {
        // A reset link expresses intent regardless of login state — it
        // may even target a different account than the current session's.
        // Skip the session loads: onAuthenticated() reloads everything
        // once the reset completes.
        resetTo('resetPassword', reset);
      } else {
        await loadSessionData();
        loadCalendarStatus(); // fire-and-forget: optional, never blocks the main flow
        consumeCalendarReturnParams();
        await resumeInProgressTaskOrGoHome();
      }
    } else {
      resetTo(reset ? 'resetPassword' : 'auth', reset || {});
    }
  } catch {
    user.value = null;
    resetTo(reset ? 'resetPassword' : 'auth', reset || {});
  } finally {
    authChecked.value = true;
  }
}

/**
 * Phase 5A: if the user (or a household member, for a shared task) left a
 * task mid-timer — status in_progress has no other way back into view,
 * since /tasks/next never returns one (see ETM_Tasks::current_task()'s
 * docblock) — resume straight into TaskScreen with the timer already
 * running instead of losing it behind a fresh start. Otherwise land on
 * the home screen (WP-3; was the category grid before the home screen
 * existed) — the resume short-circuit deliberately keeps priority.
 */
async function resumeInProgressTaskOrGoHome() {
  try {
    const res = await api.tasks.current();
    if (res.task) {
      const params = { categoryId: res.task.category_id, availableMinutes: res.task.effort_minutes };
      flow.value = params;
      currentTask.value = { task: res.task, remaining_count: 0, warnings: [] };
      resetTo('task', params);
      return;
    }
  } catch {
    // Non-fatal: worst case the timer isn't resumed and the user just
    // starts fresh from home instead.
  }
  resetTo('home');
}

/**
 * Phase 5B: the OAuth callback (ETM_Calendar_Oauth::handle_callback(), a
 * plain server redirect — see class-etm-frontend.php) lands back on this
 * same app URL with ?calendar=connected|error&provider=... . Surface the
 * result once, then strip the params so a reload doesn't re-show the toast
 * or leave OAuth bookkeeping visible in the address bar.
 */
function consumeCalendarReturnParams() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('calendar');
  if (!status) return;

  const provider = params.get('provider') === 'microsoft' ? 'Microsoft' : 'Google';
  if (status === 'connected') {
    showToast(__('%s-agenda gekoppeld.', 'etm').replace('%s', provider), 'success');
  } else {
    showToast(__('%s-agenda koppelen is niet gelukt. Probeer het opnieuw.', 'etm').replace('%s', provider), 'error');
  }

  params.delete('calendar');
  params.delete('provider');
  params.delete('reason');
  const query = params.toString();
  window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
}

/**
 * The password-reset mail's deep link lands on this same app URL with
 * ?reset_key=…&reset_login=… (built server-side by
 * ETM_Mail::reset_link()). Read them once, strip them from the address
 * bar — a reload must not re-open the form with a by-then-consumed key,
 * and the key shouldn't linger in the URL bar or browser history — and
 * let bootstrap() route to the resetPassword screen. Unlike the calendar
 * params above (logged-in branch only), these are consumed regardless of
 * login state: a reset arrives logged-out by nature. A partial link (one
 * param lost by a mangling mail client) still routes to the screen,
 * which shows its invalid-link state with a request-new-link button —
 * better than silently dropping the user on the login form.
 */
function consumeResetParams() {
  const params = new URLSearchParams(window.location.search);
  const key = params.get('reset_key');
  const login = params.get('reset_login');
  if (!key && !login) return null;

  params.delete('reset_key');
  params.delete('reset_login');
  const query = params.toString();
  window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
  return { key: key || '', login: login || '' };
}

export async function onAuthenticated(payload) {
  user.value = payload.user;
  setNonce(payload.rest_nonce);
  await loadSessionData();
  loadCalendarStatus(); // fire-and-forget, same as bootstrap() — see that call for why
  resetTo('home');
}

export async function logout() {
  try {
    await api.auth.logout();
  } finally {
    setNonce('');
    user.value = null;
    categories.value = [];
    household.value = null;
    currentTask.value = null;
    stats.value = null;
    calendarStatus.value = null;
    lastCaptureCategoryId.value = null;
    quickAddOpen.value = false;
    resetTo('auth');
  }
}

export async function loadCategories() {
  const res = await api.categories.list();
  categories.value = res.categories || [];
}

export async function loadHousehold() {
  const res = await api.household.get();
  household.value = res.household || null;
}

export async function loadStats() {
  stats.value = await api.stats.get();
}

export async function loadCalendarStatus() {
  try {
    const res = await api.calendar.status();
    calendarStatus.value = res.providers || null;
  } catch {
    // Non-fatal and purely additive (connect buttons / free-busy
    // suggestion) — never surface a toast for this, unlike
    // loadSessionData()'s categories/household which the whole app needs.
    calendarStatus.value = null;
  }
}

export async function fetchNextTask(categoryId, availableMinutes) {
  flow.value = { categoryId, availableMinutes };
  const result = await api.tasks.next({ category_id: categoryId, available_minutes: availableMinutes });
  currentTask.value = result;
  return result;
}

export async function refreshCurrentTask() {
  // categoryId 0 is the WP-3 "all categories" mode and is falsy — an
  // implicit truthiness check here would silently skip every refresh
  // (post-complete, post-skip) for the entire all-categories flow.
  const { categoryId, availableMinutes } = flow.value;
  if (categoryId === null || categoryId === undefined || !availableMinutes) return;
  return fetchNextTask(categoryId, availableMinutes);
}
